import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { useToast } from '../../context/ToastContext';

const DEFAULT_CENTER = [-1.286389, 36.817223]; // Nairobi

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

function MapViewUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom || map.getZoom());
  }, [center, zoom, map]);
  return null;
}

export default function DeliveryMapPin({
  onPinChange,
  onCountyDetected,
  initialLat,
  initialLng
}) {
  // Map center — always valid, never null
  const [mapCenter, setMapCenter] = useState(
    initialLat && initialLng ? [initialLat, initialLng] : DEFAULT_CENTER
  );
  // Pin position — null until user actively places a pin
  const [position, setPosition] = useState(
    initialLat && initialLng ? [initialLat, initialLng] : null
  );
  const label = '';
  const [locating, setLocating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const { toast } = useToast();

  const searchLocation = useCallback(async (query) => {
    if (!query || query.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=ke`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      setSearchResults(data || []);
      setShowResults(true);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchLocation(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchLocation]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    updatePin(lat, lng);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  useEffect(() => {
    if (initialLat && initialLng) {
      setPosition([initialLat, initialLng]);
      setMapCenter([initialLat, initialLng]);
      onPinChange({ lat: initialLat, lng: initialLng, label });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLat, initialLng]);

  const reverseGeocode = async (lat, lng) => {
    if (!onCountyDetected) return;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      const raw = data?.address?.state
        || data?.address?.state_district
        || data?.address?.county
        || '';
      const countyName = raw.replace(/\s*(county|city|district|municipality)$/i, '').trim();
      if (countyName) onCountyDetected(countyName);
    } catch {
      // silently fail — user can still select county manually
    }
  };

  const updatePin = (lat, lng) => {
    const newPos = [lat, lng];
    setPosition(newPos);
    setMapCenter(newPos);
    onPinChange({ lat, lng, label });
    reverseGeocode(lat, lng);
  };

  const handleMapClick = (latlng) => {
    updatePin(latlng.lat, latlng.lng);
  };

  const handleMarkerDrag = (e) => {
    const { lat, lng } = e.target.getLatLng();
    updatePin(lat, lng);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location services are not supported by your browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        updatePin(pos.coords.latitude, pos.coords.longitude);
        setLocating(false);
        toast.success('📍 Location set successfully!');
      },
      (err) => {
        setLocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          toast.error('Location permission denied. Please enable location access or pin manually.');
        } else if (err.code === err.TIMEOUT) {
          toast.error('Location request timed out. Please try again or pin manually.');
        } else {
          toast.error('Unable to retrieve your location. Please pin manually.');
        }
      },
      { timeout: 10000 }
    );
  };

  return (
    <div>
      {/* Use My Location + Search */}
      <div className="flex flex-col gap-2 mb-2">
        <button
          type="button"
          className="use-my-location-btn w-full"
          onClick={handleUseMyLocation}
        >
          {locating ? 'Locating...' : '\uD83D\uDCCD Use My Location'}
        </button>
        <div className="relative" ref={searchRef}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => { if (searchResults.length > 0) setShowResults(true); }}
            placeholder="Search for a location in Kenya..."
            className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold text-sm"
          />

          {/* Search results dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto mt-1">
              {isSearching ? (
                <div className="p-4 text-center text-sm text-gray-500">Searching...</div>
              ) : (
                searchResults.map((result, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleResultClick(result)}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <span className="text-gray-700">{result.display_name}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className="rounded-lg border border-gray-300 leaflet-map-wrapper"
        style={{ height: '280px' }}>
        <MapContainer
          center={mapCenter} zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />
          <MapClickHandler onMapClick={handleMapClick} />
          <MapViewUpdater center={mapCenter} />
          {position && (
            <Marker
              position={position}
              draggable={true}
              eventHandlers={{ dragend: handleMarkerDrag }}
            />
          )}
        </MapContainer>
      </div>

      {!position && (
        <p className="text-xs text-gray-500 mt-1">
          Click anywhere on the map or use &ldquo;Use My Location&rdquo; to pin
          your exact delivery spot.
        </p>
      )}
      {position && (
        <p className="text-xs text-green-600 mt-1">
          📍 Delivery location pinned. Drag the marker to adjust.
        </p>
      )}
    </div>
  );
}
